import { ActionReducerMapBuilder, AsyncThunk } from "@reduxjs/toolkit";
import { ApiError } from "../model/apiError";
import { applyRejection, RejectableState } from "./thunkError";

/**
 * The `catch` arm every API thunk repeats: turn an Axios failure into the
 * `ApiError` payload `applyRejection` understands.
 *
 * Generic over the return type so the caller keeps `rejectWithValue`'s exact
 * `RejectWithValue` type rather than widening to `any`.
 */
export function apiRejection<R>(
    thunkApi: { rejectWithValue: (value: ApiError) => R },
    error: any
): R {
    return thunkApi.rejectWithValue({
        message: error?.response?.data,
        statusCode: error?.response?.status
    });
}

type ApiThunk<Returned> = AsyncThunk<Returned, any, { rejectValue: ApiError }>;

/**
 * Wires the identical pending/fulfilled/rejected trio that every CRUD thunk in
 * a list slice needs: `loading` while in flight, `onFulfilled` on success, and
 * the shared `applyRejection` on failure.
 *
 * Replaces roughly a hundred lines of hand-copied `addCase` calls per slice.
 */
export function addAsyncCases<S extends RejectableState, R>(
    builder: ActionReducerMapBuilder<S>,
    thunks: ReadonlyArray<ApiThunk<R>>,
    onFulfilled: (state: S, payload: R) => void
): ActionReducerMapBuilder<S> {
    thunks.forEach(thunk => {
        builder
            .addCase(thunk.pending, state => {
                (state as unknown as RejectableState).status = "loading";
            })
            .addCase(thunk.fulfilled, (state, action) => {
                onFulfilled(state as unknown as S, action.payload);
            })
            .addCase(thunk.rejected, (state, action) => {
                applyRejection(state as unknown as RejectableState, action);
            });
    });

    return builder;
}
